import { ReactNode } from "react";
import { ifDef } from "src/common/ifDef";

import { MdmCardFormStore } from "../MdmCardFormStore";
import { ZDataNode } from "./dataTypes";
import { ZMetaField, ZMetaGrid, ZMetaNode, ZMetaTabs } from "./metaTypes";
import { Validator } from "./Validator";

interface FormItemsVisitor {
  onItem?: (item: FormItem) => void;
  onField?: (field: FormField) => void;
  onFieldEnd?: (field: FormField) => void;
  onGrid?: (grid: FormGrid) => void;
  onGridEnd?: (grid: FormGrid) => void;
  onTabs?: (tabs: FormTabs) => void;
}

export type FormItemKey = number;

export type FnFocus = (() => Promise<void>) | undefined;

export type FormItem<TMeta = ZMetaNode> = {
  key: FormItemKey;
  ownerKey: FormItemKey | null;
  meta: TMeta;
  render(form: MdmCardFormStore): ReactNode;
  walk(visitor: FormItemsVisitor): void;
  activateSubItem(subItem: FormItem): Promise<void>;
};

export type FormField = FormItem<ZMetaField> & {
  path: string;
  data: ZDataNode | null;
  setData(d: ZDataNode | null): void;
  validators: Validator[];
  focus(): Promise<void>;
};

export type FormRow = {
  basePath: string;
  item: FormItem;
};

export type FormCollapseList = FormField & {
  collapsed: boolean;
  setCollapsed(value: boolean): void;
  activeItem: number;
  setActiveItem(index: number): void;
  rows: FormRow[];
  newRow(): void;
};

export type FormGrid = FormItem<ZMetaGrid> & {
  items: FormItem[];
};

export type FormTabs = FormItem<ZMetaTabs> & {
  curKey: string;
  setCurKey(key: string): void;
  tabs: Record<string, FormItem>;
};

export const toField = (
  item: FormItem | null | undefined,
): FormField | undefined =>
  ifDef(item?.meta.type, (t) =>
    t === "field" ? (item as FormField) : undefined,
  );
